import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { formatEur, formatDate, formatDuration, MISSION_TYPE_LABEL } from '@/lib/format'

interface MissionForProvider {
  isTargeted?: boolean
  id: string
  title: string
  description: string
  type: string
  status: string
  address: string
  city: string
  postalCode: string
  date: string
  duration: number
  amount: number
  radiusKm: number
  company: { id: string; name: string; logoUrl: string | null }
  assignments: Array<{ status: string; acceptedAt: string | null }>
}

export default function MissionDisponibleDetailPage() {
  const params = useParams()
  const navigate = useNavigate()
  const missionId = params.id as string

  const [mission, setMission] = useState<MissionForProvider | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState<'accept' | 'decline' | null>(null)
  const [done, setDone] = useState<'accepted' | 'declined' | null>(null)

  useEffect(() => {
    apiFetch<MissionForProvider>(`/assignments/missions/${missionId}`)
      .then(setMission)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [missionId])

  async function handleAction(action: 'accept' | 'decline') {
    setActionLoading(action)
    setError('')
    try {
      await apiFetch(`/assignments/missions/${missionId}/${action}`, { method: 'POST' })
      setDone(action === 'accept' ? 'accepted' : 'declined')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 glass-card animate-pulse w-48" />
        <div className="h-64 glass-card animate-pulse" />
      </div>
    )
  }

  if (error && !mission) {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-4xl mb-4">⚠️</p>
        <p className="text-text-primary font-medium mb-4">{error}</p>
        <Link to="/missions-disponibles" className="glass-btn px-5 py-2.5 text-sm rounded-xl inline-block">
          ← Retour aux missions
        </Link>
      </div>
    )
  }

  if (!mission) return null

  const myAssignment = mission.assignments[0]
  const hasResponded = !!myAssignment
  const canAct = mission.status === 'OPEN' && !hasResponded && !done

  if (done === 'accepted') {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-6xl mb-5">🎉</p>
        <h1 className="text-2xl font-bold text-text-primary mb-3">Mission acceptée !</h1>
        <p className="text-text-secondary text-sm mb-2">
          <strong className="text-text-primary">{mission.title}</strong> — {mission.company.name}
        </p>
        <p className="text-text-secondary text-sm mb-6">
          Le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(mission.date))}
          {' · '}{formatEur(mission.amount)}
        </p>
        <Link to="/mes-missions" className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
          Voir mes missions →
        </Link>
      </div>
    )
  }

  if (done === 'declined') {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-5xl mb-5">👋</p>
        <h1 className="text-xl font-bold text-text-primary mb-3">Mission déclinée</h1>
        <p className="text-text-secondary text-sm mb-6">Vous pourrez voir d'autres missions disponibles.</p>
        <Link to="/missions-disponibles" className="glass-btn px-6 py-3 text-sm rounded-xl inline-block">
          ← Retour aux missions
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Link to="/missions-disponibles" className="text-text-muted hover:text-text-secondary text-sm transition-colors">
          ←
        </Link>
        <div>
          <p className="text-text-muted text-xs mb-0.5">{mission.company.name}</p>
          <h1 className="text-2xl font-bold text-text-primary">{mission.title}</h1>
        </div>
      </div>

      {mission.isTargeted && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-warning/10 border border-warning/30 text-warning text-sm">
          ⭐ <strong>{mission.company.name} vous a choisi</strong> pour cette mission. Elle vous est réservée pendant 15 minutes après publication — au-delà, elle est proposée aux autres prestataires qualifiés.
        </div>
      )}

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
          {error}
        </div>
      )}

      {/* Carte budget — mise en avant */}
      <div className="glass-card p-6 mb-4 flex items-center justify-between">
        <div>
          <p className="text-text-muted text-xs mb-1">Budget proposé</p>
          <p className="text-4xl font-bold text-[#8B9AFF]">{formatEur(mission.amount)}</p>
        </div>
        <div className="text-right">
          <p className="text-text-muted text-xs mb-1">Durée</p>
          <p className="text-text-primary font-semibold text-xl">{formatDuration(mission.duration)}</p>
        </div>
      </div>

      {/* Infos */}
      <div className="glass-card p-6 mb-4">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Détails</h2>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          {[
            { icon: '📅', label: 'Date', value: formatDate(mission.date) },
            { icon: '🏷', label: 'Type', value: MISSION_TYPE_LABEL[mission.type] ?? mission.type },
            { icon: '📍', label: 'Lieu', value: `${mission.address}, ${mission.city} ${mission.postalCode}` },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <span className="text-xl">{item.icon}</span>
              <div>
                <p className="text-text-muted text-xs">{item.label}</p>
                <p className="text-text-primary font-medium mt-0.5">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Description</h2>
        <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">{mission.description}</p>
      </div>

      {/* Actions */}
      {canAct ? (
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleAction('accept')}
            disabled={!!actionLoading}
            className="glass-btn-accent flex-1 py-4 text-base rounded-xl font-semibold"
          >
            {actionLoading === 'accept' ? 'Confirmation…' : '✓ Accepter la mission'}
          </button>
          <button
            onClick={() => handleAction('decline')}
            disabled={!!actionLoading}
            className="glass-btn px-6 py-4 text-sm rounded-xl text-text-secondary"
          >
            {actionLoading === 'decline' ? '…' : 'Décliner'}
          </button>
        </div>
      ) : hasResponded ? (
        <div className="glass-card p-4 text-center text-text-secondary text-sm">
          {myAssignment.status === 'ACCEPTED' ? '✅ Vous avez accepté cette mission' : '❌ Vous avez décliné cette mission'}
        </div>
      ) : (
        <div className="glass-card p-4 text-center text-text-secondary text-sm">
          Cette mission n'est plus disponible
        </div>
      )}
    </div>
  )
}
